function joinUrl(baseUrl, path) {
  const base = String(baseUrl ?? '').replace(/\/$/, '');
  return `${base}${path}`;
}

function makeHeaders(accessToken, extra = {}) {
  return {
    Accept: 'application/json',
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...extra
  };
}

async function readJson(response) {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

async function request({ baseUrl, path, accessToken, method = 'GET', body, signal }) {
  const response = await fetch(joinUrl(baseUrl, path), {
    method,
    credentials: 'include',
    headers: makeHeaders(
      accessToken,
      body ? { 'Content-Type': 'application/json' } : {}
    ),
    body: body ? JSON.stringify(body) : undefined,
    signal
  });

  const payload = await readJson(response);
  const correlationId = response.headers.get('X-Correlation-ID');

  if (!response.ok) {
    const error = new Error(payload?.message || `Request failed (${response.status}).`);
    error.status = response.status;
    error.correlationId = payload?.correlationId ?? correlationId ?? null;
    error.payload = payload;
    throw error;
  }

  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    return {
      ...payload,
      _response: {
        ...(payload._response ?? {}),
        correlationId: correlationId ?? null
      }
    };
  }

  return payload;
}

export function createCopilotApi({ baseUrl = '', accessToken }) {
  return {
    me(signal) {
      return request({ baseUrl, path: '/api/auth/me', accessToken, signal });
    },
    orderContext(orderNo, purpose = 'order_status', signal) {
      const encodedOrder = encodeURIComponent(orderNo);
      const encodedPurpose = encodeURIComponent(purpose);
      return request({
        baseUrl,
        path: `/api/orders/${encodedOrder}/context?purpose=${encodedPurpose}`,
        accessToken,
        signal
      });
    },
    ask(orderNo, question, signal) {
      return request({
        baseUrl,
        path: buildUnifiedAskPath(orderNo),
        accessToken,
        method: 'POST',
        body: { question },
        signal
      });
    },
    operationsRadar(maxOrders = 500, signal) {
      return request({
        baseUrl,
        path: buildOperationsRadarPath(maxOrders),
        accessToken,
        signal
      });
    },
    operationsRadarBrief(question, maxOrders = 500, signal) {
      return request({
        baseUrl,
        path: buildOperationsRadarBriefPath(maxOrders),
        accessToken,
        method: 'POST',
        body: { question },
        signal
      });
    },
    submitPilotFeedback(targetCorrelationId, rating, category, signal) {
      return request({
        baseUrl,
        path: buildPilotFeedbackPath(),
        accessToken,
        method: 'POST',
        body: {
          targetCorrelationId,
          rating,
          category: category || null
        },
        signal
      });
    }
  };
}

export function buildUnifiedAskPath(orderNo) {
  return `/api/copilot/orders/${encodeURIComponent(orderNo)}/ask-unified`;
}

export function buildOperationsRadarPath(maxOrders = 500) {
  const bounded = Math.max(1, Math.min(500, Number(maxOrders) || 500));
  return `/api/operations/radar?maxOrders=${bounded}`;
}

export function buildOperationsRadarBriefPath(maxOrders = 500) {
  const bounded = Math.max(1, Math.min(500, Number(maxOrders) || 500));
  return `/api/operations/radar/brief?maxOrders=${bounded}`;
}

export function buildPilotFeedbackPath() {
  return '/api/pilot/feedback';
}
